/**
 * Snapshot export + import store.
 *
 * Owns the conflict-dialog state (an in-flight snapshot waiting on user
 * resolution) plus a flat `lastReport` for the UI to show counts. The
 * heavy lifting lives in `src/importers/` — this store is a thin reactive
 * shell that hands repos to the importer functions.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import {
  ChargingProfileRepositoryDexie,
  ConfigurationRepositoryDexie,
  LocalAuthListRepositoryDexie,
  LocalAuthListVersionRepositoryDexie,
  OfflineQueueRepositoryDexie,
  SnapshotRepositoryDexie,
  StationRepositoryDexie,
  VariableValuesRepositoryDexie,
} from '@/persistence/dexie/repositories';
import type { StationSnapshot } from '@/domain';
import type { StationId } from '@/domain/types';
import type { SnapshotKind } from '@/persistence/ports';
import { useStationStore } from '@/state/useStationStore';
import {
  applySnapshot,
  buildSnapshot,
  ConflictResolution,
  detectConflict,
  parseSnapshotV1,
  snapshotFilename,
  snapshotToJson,
  type ConflictChoice,
  type ConflictDetected,
  type ImportReport,
  type ImportRepos,
  type SnapshotV1,
} from '@/importers';

const stationRepo = new StationRepositoryDexie(db);
const configRepo = new ConfigurationRepositoryDexie(db);
const profileRepo = new ChargingProfileRepositoryDexie(db);
const offlineRepo = new OfflineQueueRepositoryDexie(db);
const snapshotRepo = new SnapshotRepositoryDexie(db);
const varValuesRepo = new VariableValuesRepositoryDexie(db);
const localAuthListRepo = new LocalAuthListRepositoryDexie(db);
const localAuthVersionRepo = new LocalAuthListVersionRepositoryDexie(db);

const repos: ImportRepos = {
  stations: stationRepo,
  configurations: configRepo,
  chargingProfiles: profileRepo,
  offlineQueue: offlineRepo,
  snapshots: snapshotRepo,
  variableValues: varValuesRepo,
  localAuthList: localAuthListRepo,
  localAuthListVersion: localAuthVersionRepo,
};

export interface PendingImport {
  readonly snapshot: SnapshotV1;
  readonly conflict: ConflictDetected;
}

export interface AuditEntry {
  readonly id: string;
  readonly kind: SnapshotKind;
  readonly createdAt: number;
  readonly cpid: string;
}

export interface SnapshotStoreState {
  readonly status: 'idle' | 'parsing' | 'awaiting-conflict' | 'importing' | 'done' | 'error';
  readonly pending: PendingImport | null;
  readonly lastReport: ImportReport | null;
  readonly lastError: string | null;
  readonly validationErrors: readonly string[];
  /** Snapshots audit history for the current station (most recent first). */
  readonly audit: readonly AuditEntry[];
}

export interface ExportOverrides {
  readonly include?: {
    readonly chargingProfiles?: boolean;
    readonly offlineQueue?: boolean;
    readonly deviceModel?: boolean;
    readonly localAuthList?: boolean;
  };
}

export interface SnapshotStoreActions {
  /**
   * Build a v1 snapshot for the station identified by `stationId`, pulling
   * the latest configuration / profiles / offlineQueue from their Dexie
   * repos, and trigger a browser download.
   */
  exportNow(stationId: StationId, overrides?: ExportOverrides): Promise<void>;
  /**
   * Parse + validate raw JSON. If a (cpid, wsUrl) match exists, transitions
   * the store to `awaiting-conflict`. Otherwise applies the snapshot
   * immediately and reports.
   */
  importFromText(raw: string): Promise<void>;
  /** Resolve a pending conflict with one of Replace / SaveAsCopy / Cancel. */
  resolvePending(choice: ConflictChoice): Promise<void>;
  /** Drop the pending conflict + reset to idle (Cancel-style). */
  cancelPending(): void;
  /** Refresh the audit list from Dexie for the given station. */
  refreshAudit(stationId: StationId): Promise<void>;
}

export type SnapshotStore = SnapshotStoreState & SnapshotStoreActions;

function readCpid(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '—';
  const p = payload as { station?: { cpid?: unknown } };
  const cpid = p.station?.cpid;
  return typeof cpid === 'string' ? cpid : '—';
}

export const useSnapshotStore = create<SnapshotStore>((set, get) => ({
  status: 'idle',
  pending: null,
  lastReport: null,
  lastError: null,
  validationErrors: [],
  audit: [],

  async exportNow(stationId, overrides): Promise<void> {
    // Prefer the persisted snapshot; fall back to projecting from the live
    // useStationStore so a user can export a brand-new station before its
    // first successful boot has had the chance to write the Dexie row. In
    // the fallback case we also persist the station — exporting expresses
    // intent to manage this station, and import-side conflict detection
    // needs the row to be visible to findByIdentity later.
    let station = await stationRepo.get(stationId);
    if (!station) {
      station = projectStationFromLive(stationId);
      if (!station) {
        set({ status: 'error', lastError: `No station found for id=${stationId}` });
        return;
      }
      await stationRepo.upsert(station);
    }
    const [configuration, chargingProfiles, offlineQueue, variableValues, localAuthEntries, localAuthVersionRow] =
      await Promise.all([
        configRepo.byStation(stationId),
        profileRepo.byStation(stationId),
        offlineRepo.byStation(stationId),
        varValuesRepo.byStation(stationId),
        localAuthListRepo.byStation(stationId),
        localAuthVersionRepo.get(stationId),
      ]);
    const snap = buildSnapshot({
      station,
      configuration,
      chargingProfiles,
      offlineQueue,
      variableValues,
      localAuthList: { version: localAuthVersionRow?.listVersion ?? 0, entries: localAuthEntries },
      exportedBy: 'VirtualCharger.app@0.1.0',
      ...(overrides?.include !== undefined ? { include: overrides.include } : {}),
    });
    const text = snapshotToJson(snap);
    triggerDownload(snapshotFilename(station.cpid), text);

    // Audit-trail row for exports too — mirrors the import path so the
    // snapshots table is a unified history.
    await snapshotRepo.upsert({
      id: globalThis.crypto?.randomUUID?.() ?? `export-${Date.now()}`,
      stationId: station.id,
      createdAt: Date.now(),
      kind: 'export',
      payload: snap,
    });
    set({ status: 'done', lastReport: null });
    await get().refreshAudit(stationId);
  },

  async importFromText(raw: string): Promise<void> {
    set({ status: 'parsing', pending: null, lastError: null, validationErrors: [] });
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', lastError: `Invalid JSON: ${msg}` });
      return;
    }
    const result = parseSnapshotV1(parsed);
    if (!result.ok) {
      set({ status: 'error', validationErrors: result.errors, lastError: 'Snapshot failed validation.' });
      return;
    }
    const conflict = await detectConflict(stationRepo, result.value);
    if (conflict.kind === 'conflict') {
      set({ status: 'awaiting-conflict', pending: { snapshot: result.value, conflict } });
      return;
    }
    set({ status: 'importing' });
    try {
      const report = await applySnapshot(result.value, repos);
      set({ status: 'done', lastReport: report, pending: null });
      await get().refreshAudit(report.stationId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', lastError: msg });
    }
  },

  async resolvePending(choice: ConflictChoice): Promise<void> {
    const pending = get().pending;
    if (!pending) return;
    if (choice.resolution === ConflictResolution.Cancel) {
      set({ status: 'idle', pending: null });
      return;
    }
    set({ status: 'importing' });
    try {
      const report = await applySnapshot(pending.snapshot, repos, {
        conflict: { conflict: pending.conflict, choice },
      });
      set({ status: 'done', lastReport: report, pending: null });
      await get().refreshAudit(report.stationId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', lastError: msg });
    }
  },

  cancelPending(): void {
    set({ status: 'idle', pending: null });
  },

  async refreshAudit(stationId): Promise<void> {
    const rows = await snapshotRepo.byStation(stationId);
    const next: AuditEntry[] = rows
      .map((r) => ({
        id: r.id,
        kind: r.kind,
        createdAt: r.createdAt,
        cpid: readCpid(r.payload),
      }))
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 20);
    set({ audit: next });
  },
}));

function projectStationFromLive(stationId: StationId): StationSnapshot | null {
  const live = useStationStore.getState();
  if (live.stationId !== stationId) return null;
  if (live.cpid === '' || live.wsUrl === '') return null;
  return {
    id: live.stationId,
    cpid: live.cpid,
    wsUrl: live.wsUrl,
    ocppVersion: live.ocppVersion,
    topology: { cpMaxPowerW: live.cpMaxPowerW, evses: live.evses },
    createdAt: Date.now(),
  };
}

function triggerDownload(filename: string, text: string): void {
  // Browser only — guarded so unit tests in Node don't trip on document.
  if (typeof document === 'undefined') return;
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Stable identifier the e2e harness uses to confirm the export path ran. */
export function getLastExportFilename(cpid: string, at?: number): string {
  return snapshotFilename(cpid, at);
}

// Re-export for UI convenience.
export { ConflictResolution } from '@/importers';
